/**
 * Tag State Atoms
 *
 * Jotai atoms for tag filters, selection, and view preferences.
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { TagFilter, TagType, TagStatus } from '@/schemas/tag/tag.schemas';

// ============================================================================
// Filter Atoms
// ============================================================================

export const tagFiltersAtom = atomWithStorage<TagFilter>(
  'tag-filters',
  { factoryId: '', page: 1, limit: 20 },
  undefined,
  { getOnInit: true }
);

export const tagSearchQueryAtom = atom<string>('');
export const tagTypeFilterAtom = atom<TagType | undefined>(undefined);
export const tagStatusFilterAtom = atom<TagStatus | undefined>(undefined);

/** Combined filters for API query */
export const tagQueryFiltersAtom = atom(get => {
  const baseFilters = get(tagFiltersAtom);
  const searchQuery = get(tagSearchQueryAtom);
  const typeFilter = get(tagTypeFilterAtom);
  const statusFilter = get(tagStatusFilterAtom);

  return {
    ...baseFilters,
    ...(searchQuery && { search: searchQuery }),
    ...(typeFilter && { tagType: typeFilter }),
    ...(statusFilter && { status: statusFilter }),
  } as TagFilter;
});

// ============================================================================
// Selection Atoms
// ============================================================================

export const selectedTagIdsAtom = atom<Set<string>>(new Set<string>());

export const isTagSelectedAtom = atom(get => (tagId: string): boolean => {
  return get(selectedTagIdsAtom).has(tagId);
});

export const selectedTagCountAtom = atom(get => get(selectedTagIdsAtom).size);

export const toggleTagSelectionAtom = atom(null, (get, set, tagId: string) => {
  const selected = get(selectedTagIdsAtom);
  const newSelected = new Set(selected);
  if (newSelected.has(tagId)) {
    newSelected.delete(tagId);
  } else {
    newSelected.add(tagId);
  }
  set(selectedTagIdsAtom, newSelected);
});

export const clearTagSelectionAtom = atom(null, (_get, set) => {
  set(selectedTagIdsAtom, new Set());
});

export const selectAllTagsAtom = atom(null, (_get, set, tagIds: string[]) => {
  set(selectedTagIdsAtom, new Set(tagIds));
});

// ============================================================================
// Reset Helpers
// ============================================================================

export const resetTagStateAtom = atom(null, (_get, set) => {
  set(tagSearchQueryAtom, '');
  set(selectedTagIdsAtom, new Set<string>());
});

export const resetTagFiltersAtom = atom(null, (get, set) => {
  const currentFilters = get(tagFiltersAtom) as TagFilter;
  set(tagFiltersAtom, {
    factoryId: currentFilters.factoryId,
    page: 1,
    limit: 20,
  });
  set(tagSearchQueryAtom, '');
});

export const resetTagSelectionAtom = atom(null, (_get, set) => {
  set(selectedTagIdsAtom, new Set<string>());
});
